import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Folder from "@mui/icons-material/Folder";
import SubdirectoryArrowRight from "@mui/icons-material/SubdirectoryArrowRight";
import Undo from "@mui/icons-material/Undo";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { contributorAccess, descendantsOf, levelLabels, plural } from "../access-model";
import { LevelSelect } from "./level-select";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { AccessIndex, Level } from "../access-model";
import type { AccessPerson } from "../hooks/use-program-access";
import type { FC } from "react";

/** One contributor's level on a program and on each program below it. */
export const ContributorSubfolders: FC<{
  tree: ProgramTree;
  program: ProgramNode;
  person: AccessPerson;
  index: AccessIndex;
  busy: boolean;
  onLevel: (program: ProgramNode, level: Level) => void;
}> = ({ tree, program, person, index, busy, onLevel }) => {
  const rows = [{ node: program, depth: 0 }, ...descendantsOf(tree, program.id)];
  const top = contributorAccess(index, tree, person.id, program.id);

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {top.access.level === "none"
          ? `${person.name} can only see the name of ${program.title}. Their access is set on the subfolders below.`
          : `${person.name} has ${levelLabels[top.access.level]} on ${program.title}${
              top.exceptions
                ? `, with ${plural(top.exceptions, "exception")} below.`
                : ", and every subfolder follows it."
            }`}
      </Typography>
      <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {rows.map(({ node, depth }) => {
          const { access, below } = contributorAccess(index, tree, person.id, node.id);
          const isSet = access.setLevel !== null;
          const from = access.inheritedFrom
            ? tree.byId.get(access.inheritedFrom)?.title
            : undefined;
          const canReset = isSet && (depth > 0 || Boolean(node.parent_id));

          return (
            <Box
              component="li"
              key={node.id}
              sx={{ display: "flex", alignItems: "center", gap: 1, pl: depth * 3.5 }}
            >
              {depth ? (
                <SubdirectoryArrowRight fontSize="small" sx={{ color: "text.disabled" }} />
              ) : (
                <Folder fontSize="small" sx={{ color: "text.secondary" }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: depth ? 400 : 600 }}>
                  {node.title}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  {isSet ? (
                    <Chip size="small" color="primary" variant="outlined" label="Set here" />
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}
                    >
                      <ArrowDownward sx={{ fontSize: 14, mr: 0.5 }} />
                      {from ? `Inherited from ${from}` : "No access by default"}
                    </Typography>
                  )}
                  {access.level === "none" && below > 0 ? (
                    <Chip size="small" color="warning" variant="outlined" label="Name only" />
                  ) : null}
                </Stack>
              </Box>
              {canReset ? (
                <Tooltip
                  title={`Reset ${node.title} to inherited (${levelLabels[access.inheritedLevel]})`}
                >
                  <IconButton
                    aria-label={`Reset ${node.title} to inherited (${levelLabels[access.inheritedLevel]})`}
                    disabled={busy}
                    onClick={() => onLevel(node, access.inheritedLevel)}
                    data-analytics-id="program-access-reset"
                  >
                    <Undo fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <LevelSelect
                label={`Access level for ${person.name} in ${node.title}`}
                level={access.level}
                disabled={busy}
                onChange={(level) => onLevel(node, level)}
              />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
